import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  collection, getDocs, addDoc, doc, setDoc, deleteDoc,
  query, orderBy, serverTimestamp, onSnapshot, limit,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../firebase/firebase-config";
import { logAction } from "../firebase/logAction";
import {
  FaMicrophone, FaStop, FaPause, FaPlay, FaTrash,
  FaEdit, FaUpload, FaPlus, FaPodcast, FaCheck,
  FaUndo, FaRedo, FaImage, FaMapMarkerAlt, FaCalendarAlt, FaDownload,
  FaBroadcastTower,
} from "react-icons/fa";

const fmtTime = (s) => {
  if (!s || !isFinite(s) || isNaN(s)) return "--:--";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const fmtDuration = (s) => {
  if (!s || !isFinite(s) || isNaN(s)) return '';
  const mins = Math.round(s / 60);
  return mins < 1 ? '< 1 min' : `${mins} min`;
};

const BLANK_FORM = {
  title: "", episodeNum: "", season: "1", guest: "", guestTitle: "", guestBio: "",
  description: "", showNotes: "", tags: "", published: false, coverArt: "",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateTimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function PodcastStudio() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  /* recording */
  const [recState, setRecState] = useState("idle");
  const [recTime, setRecTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [vuLevels, setVuLevels] = useState(Array(16).fill(0));
  const [micError, setMicError] = useState("");
  const [boothLocation, setBoothLocation] = useState("");
  const [recordedAt, setRecordedAt] = useState(null);

  /* upload */
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  /* editor */
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [pendingAudio, setPendingAudio] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [liveSession, setLiveSession] = useState(null);
  const [liveTitle, setLiveTitle] = useState("Live from The PP Podcast");
  const [liveScheduleAt, setLiveScheduleAt] = useState("");
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const [liveComments, setLiveComments] = useState([]);
  const [liveListenerCount, setLiveListenerCount] = useState(0);
  const [liveElapsed, setLiveElapsed] = useState(0);
  const liveScheduled = !!liveSession?.scheduledAt && !liveSession?.isLive;

  /* ── episode player ── */
  const [playerEp, setPlayerEp] = useState(null);
  const [playerPlaying, setPlayerPlaying] = useState(false);
  const [playerTime, setPlayerTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [playerSpeed, setPlayerSpeed] = useState(1);

  /* refs */
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const animRef = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(new Audio());
  const playerAudioRef = useRef(new Audio());
  const uploadInputRef = useRef(null);
  const coverUploadRef = useRef(null);
  const progressRef = useRef(null);
  const liveRoomRef = useRef(null);

  /* ── load episodes ── */
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "podcast_episodes"), orderBy("episodeNum", "desc")));
        setEpisodes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch { /* empty */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "podcast_live", "current"), snap => {
      const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      setLiveSession(data);
      if (data?.title) setLiveTitle(data.title);
      if (data?.scheduledAt) setLiveScheduleAt(fmtDateTimeLocal(data.scheduledAt));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const commentsQuery = query(collection(db, "podcast_live", "current", "comments"), orderBy("createdAt", "desc"), limit(30));
    const unsubComments = onSnapshot(commentsQuery, snap => {
      setLiveComments(snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse());
    });
    const unsubListeners = onSnapshot(collection(db, "podcast_live", "current", "listeners"), snap => {
      const now = Date.now();
      const active = snap.docs.filter(d => {
        const data = d.data();
        const lastSeen = toDate(data.lastSeen)?.getTime() || 0;
        return data.status === "connected" && now - lastSeen < 45000;
      }).length;
      setLiveListenerCount(active);
    });
    return () => {
      unsubComments();
      unsubListeners();
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      const started = toDate(liveSession?.startedAt)?.getTime();
      setLiveElapsed(started ? Math.max(0, Math.floor((Date.now() - started) / 1000)) : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [liveSession?.startedAt]);

  /* ── wire up episode player audio events ── */
  useEffect(() => {
    const audio = playerAudioRef.current;
    const onTime     = () => setPlayerTime(audio.currentTime);
    const onMeta     = () => setPlayerDuration(isNaN(audio.duration) || !isFinite(audio.duration) ? 0 : audio.duration);
    const onEnded    = () => { setPlayerPlaying(false); setPlayerTime(0); };
    const onPlay     = () => setPlayerPlaying(true);
    const onPause    = () => setPlayerPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, []);

  /* ── toast helper ── */
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  /* ── waveform draw ── */
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    const bufLen = analyser.frequencyBinCount;
    const timeData = new Uint8Array(bufLen);
    const freqData = new Uint8Array(bufLen);

    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(timeData);
      analyser.getByteFrequencyData(freqData);

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#05080f";
      ctx.fillRect(0, 0, W, H);

      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      for (let i = 1; i < 4; i++) {
        ctx.beginPath(); ctx.moveTo(0, (H / 4) * i); ctx.lineTo(W, (H / 4) * i); ctx.stroke();
      }

      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#22c55e");
      grad.addColorStop(0.5, "#4ade80");
      grad.addColorStop(1, "#22c55e");
      ctx.lineWidth = 2;
      ctx.strokeStyle = grad;
      ctx.shadowColor = "#22c55e";
      ctx.shadowBlur = 10;
      ctx.beginPath();
      const sliceW = W / bufLen;
      let x = 0;
      for (let i = 0; i < bufLen; i++) {
        const v = timeData[i] / 128;
        const y = (v * H) / 2;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        x += sliceW;
      }
      ctx.lineTo(W, H / 2);
      ctx.stroke();
      ctx.shadowBlur = 0;

      const chunk = Math.floor(bufLen / 16);
      setVuLevels(Array.from({ length: 16 }, (_, i) => {
        const sl = freqData.slice(i * chunk, (i + 1) * chunk);
        return sl.reduce((a, b) => a + b, 0) / sl.length / 255;
      }));
    };
    draw();
  }, []);

  const drawFlatLine = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#05080f";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      ctx.beginPath(); ctx.moveTo(0, (H / 4) * i); ctx.lineTo(W, (H / 4) * i); ctx.stroke();
    }
    ctx.strokeStyle = "rgba(74,222,128,0.2)";
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(0, H / 2); ctx.lineTo(W, H / 2); ctx.stroke();
  }, []);

  useEffect(() => { drawFlatLine(); }, [drawFlatLine]);

  /* ── cleanup on unmount ── */
  useEffect(() => () => {
    cancelAnimationFrame(animRef.current);
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    audioCtxRef.current?.close();
    audioRef.current.pause();
    playerAudioRef.current.pause();
    liveRoomRef.current?.disconnect();
  }, []);

  const requestLivekitToken = async (role) => {
    const headers = { "Content-Type": "application/json" };
    if (role === "host") {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Please sign in again before starting live.");
      headers.Authorization = `Bearer ${token}`;
    }
    const res = await fetch("/api/livekit/token", {
      method: "POST",
      headers,
      body: JSON.stringify({ role }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Live token request failed.");
    return data;
  };

  const clearLiveRoomState = async () => {
    const groups = ["comments", "listeners"];
    for (const group of groups) {
      const snap = await getDocs(collection(db, "podcast_live", "current", group));
      await Promise.all(snap.docs.map(item => deleteDoc(item.ref)));
    }
  };

  const startLivePodcast = async () => {
    if (liveLoading || liveConnected) return;
    setLiveLoading(true);
    setMicError("");
    try {
      const { Room, RoomEvent } = await import("livekit-client");
      const { token, url, roomName } = await requestLivekitToken("host");
      const room = new Room({ adaptiveStream: true, dynacast: true });
      room.on(RoomEvent.Disconnected, () => {
        setLiveConnected(false);
        liveRoomRef.current = null;
      });
      await room.connect(url, token);
      await room.localParticipant.setMicrophoneEnabled(true);
      liveRoomRef.current = room;
      setLiveConnected(true);
      await clearLiveRoomState();
      await setDoc(doc(db, "podcast_live", "current"), {
        isLive: true,
        status: "live",
        title: liveTitle.trim() || "Live from The PP Podcast",
        scheduledAt: liveScheduleAt ? new Date(liveScheduleAt).toISOString() : null,
        roomName,
        host: "Pooja",
        startedAt: serverTimestamp(),
        endedAt: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      showToast("Live podcast started.");
      logAction("podcast.live.start", liveTitle.trim() || "Live podcast", null);
    } catch (err) {
      setMicError(err.message || "Could not start live podcast.");
    } finally {
      setLiveLoading(false);
    }
  };

  const stopLivePodcast = async () => {
    if (liveLoading) return;
    setLiveLoading(true);
    try {
      liveRoomRef.current?.disconnect();
      liveRoomRef.current = null;
      setLiveConnected(false);
      const started = toDate(liveSession?.startedAt);
      const ended = new Date();
      await addDoc(collection(db, "podcast_live_sessions"), {
        title: liveSession?.title || liveTitle.trim() || "Live podcast",
        scheduledAt: liveSession?.scheduledAt || (liveScheduleAt ? new Date(liveScheduleAt).toISOString() : null),
        startedAt: liveSession?.startedAt || null,
        endedAt: serverTimestamp(),
        duration: started ? Math.max(0, Math.round((ended.getTime() - started.getTime()) / 1000)) : 0,
        listenerCount: liveListenerCount,
        commentCount: liveComments.length,
        comments: liveComments.map(c => ({
          name: c.name || "Listener",
          text: c.text || "",
        })),
        source: "livekit",
        createdAt: serverTimestamp(),
      });
      await setDoc(doc(db, "podcast_live", "current"), {
        isLive: false,
        status: "ended",
        endedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      showToast("Live podcast ended.");
      logAction("podcast.live.end", liveTitle.trim() || "Live podcast", null);
    } catch (err) {
      setMicError(err.message || "Could not stop live podcast.");
    } finally {
      setLiveLoading(false);
    }
  };

  const saveLiveSchedule = async () => {
    if (!liveScheduleAt) {
      showToast("Pick a date and time first.");
      return;
    }
    setLiveLoading(true);
    try {
      await clearLiveRoomState();
      await setDoc(doc(db, "podcast_live", "current"), {
        isLive: false,
        status: "scheduled",
        title: liveTitle.trim() || "Live from The PP Podcast",
        scheduledAt: liveScheduleAt ? new Date(liveScheduleAt).toISOString() : null,
        host: "Pooja",
        startedAt: null,
        endedAt: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      showToast("Live schedule saved.");
      logAction("podcast.live.schedule", liveTitle.trim() || "Live podcast", null);
    } catch (err) {
      showToast("Schedule failed — " + err.message);
    } finally {
      setLiveLoading(false);
    }
  };

  const cancelLiveSchedule = async () => {
    if (!window.confirm("Cancel the scheduled live session?")) return;
    setLiveLoading(true);
    try {
      await clearLiveRoomState();
      await setDoc(doc(db, "podcast_live", "current"), {
        isLive: false,
        status: "idle",
        scheduledAt: null,
        startedAt: null,
        endedAt: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setLiveScheduleAt("");
      showToast("Live schedule cancelled.");
      logAction("podcast.live.cancel", liveTitle.trim() || "Live podcast", null);
    } catch (err) {
      showToast("Cancel failed — " + err.message);
    } finally {
      setLiveLoading(false);
    }
  };

  /* ── recording ── */
  const startRecording = async () => {
    setMicError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        audioRef.current.src = url;
        audioRef.current.onloadedmetadata = () => {
          const d = audioRef.current.duration;
          if (d && !isNaN(d) && isFinite(d)) setRecTime(Math.round(d));
        };
        cancelAnimationFrame(animRef.current);
        setVuLevels(Array(16).fill(0));
        drawFlatLine();
      };
      mr.start(100);
      mediaRecorderRef.current = mr;
      setRecState("recording");
      setRecTime(0);
      setRecordedAt(new Date().toISOString());
      timerRef.current = setInterval(() => setRecTime(t => t + 1), 1000);
      drawWaveform();
    } catch {
      setMicError("Microphone access denied. Please allow microphone access in your browser.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
      cancelAnimationFrame(animRef.current);
      setRecState("paused");
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => setRecTime(t => t + 1), 1000);
      drawWaveform();
      setRecState("recording");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    audioCtxRef.current?.close().catch(() => {});
    setRecState("done");
  };

  const discardRecording = () => {
    setAudioBlob(null); setAudioUrl(null); setRecTime(0);
    setRecordedAt(null);
    setRecState("idle"); drawFlatLine();
  };

  const togglePlay = () => {
    if (!audioUrl) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); audioRef.current.onended = () => setPlaying(false); }
  };

  /* ── episode player controls ── */
  const loadEpisode = useCallback((ep) => {
    if (!ep.audioUrl) return;
    if (playerEp?.id === ep.id) {
      if (playerPlaying) { playerAudioRef.current.pause(); }
      else { playerAudioRef.current.play().catch(() => {}); }
      return;
    }
    playerAudioRef.current.pause();
    playerAudioRef.current.src = ep.audioUrl;
    playerAudioRef.current.playbackRate = playerSpeed;
    playerAudioRef.current.load();
    playerAudioRef.current.play().catch(() => {});
    setPlayerEp(ep);
    setPlayerTime(0);
    setPlayerDuration(ep.duration || 0);
  }, [playerEp, playerPlaying, playerSpeed]);

  const changeSpeed = (s) => {
    setPlayerSpeed(s);
    playerAudioRef.current.playbackRate = s;
  };

  const togglePlayerPlay = () => {
    if (!playerEp) return;
    if (playerPlaying) { playerAudioRef.current.pause(); }
    else { playerAudioRef.current.play().catch(() => {}); }
  };

  const skipPlayer = (secs) => {
    const audio = playerAudioRef.current;
    if (!audio.src) return;
    audio.currentTime = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + secs));
  };

  const seekPlayer = (e) => {
    const bar = progressRef.current;
    if (!bar || !playerDuration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    playerAudioRef.current.currentTime = pct * playerDuration;
  };

  const closePlayer = () => {
    playerAudioRef.current.pause();
    playerAudioRef.current.src = "";
    setPlayerEp(null);
    setPlayerPlaying(false);
    setPlayerTime(0);
    setPlayerDuration(0);
  };

  /* ── upload ── */
  const uploadAudio = (blob, fileName) =>
    new Promise((resolve, reject) => {
      const storageRef = ref(storage, `podcast/${fileName}`);
      const task = uploadBytesResumable(storageRef, blob);
      task.on("state_changed",
        snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        async () => { resolve(await getDownloadURL(task.snapshot.ref)); }
      );
    });

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setAudioBlob(file); setAudioUrl(url);
    audioRef.current.src = url;
    setRecState("done");
    audioRef.current.onloadedmetadata = () => {
      const d = audioRef.current.duration;
      if (isFinite(d) && d > 0) {
        setRecTime(Math.round(d));
      } else {
        /* VBR MP3: seek to large offset forces browser to compute real duration */
        audioRef.current.currentTime = 1e101;
      }
    };
    audioRef.current.ontimeupdate = () => {
      const d = audioRef.current.duration;
      if (isFinite(d) && d > 0) {
        setRecTime(Math.round(d));
        audioRef.current.ontimeupdate = null;
        audioRef.current.currentTime = 0;
      }
    };
  };

  const handleCoverUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const storageRef = ref(storage, `podcast-art/${Date.now()}-${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on("state_changed",
      snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => { showToast("Cover upload failed — " + err.message); setUploading(false); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setf("coverArt", url);
        setUploading(false);
        setUploadPct(0);
      }
    );
  };

  /* ── editor ── */
  const openNewEpisode = () => {
    setEditingId(null);
    setForm({ ...BLANK_FORM, episodeNum: String((episodes[0]?.episodeNum || 0) + 1) });
    setPendingAudio(audioBlob ? { blob: audioBlob, url: audioUrl } : null);
    setShowEditor(true);
  };

  const openEditEpisode = (ep) => {
    setEditingId(ep.id);
    setForm({
      title: ep.title || "",
      episodeNum: String(ep.episodeNum || ""),
      season: String(ep.season || "1"),
      guest: ep.guest || "",
      guestTitle: ep.guestTitle || "",
      guestBio: ep.guestBio || "",
      description: ep.description || "",
      showNotes: ep.showNotes || "",
      tags: (ep.tags || []).join(", "),
      published: !!ep.published,
      coverArt: ep.coverArt || "",
    });
    setPendingAudio(null);
    setShowEditor(true);
  };

  const saveEpisode = async () => {
    if (!form.title.trim()) { showToast("Please add an episode title."); return; }
    setSaving(true);
    try {
      const existingEp = editingId ? episodes.find(e => e.id === editingId) : null;
      let audioUrlFinal = existingEp?.audioUrl || "";

      if (pendingAudio?.blob) {
        setUploading(true);
        const fileName = `ep-${Date.now()}.webm`;
        audioUrlFinal = await uploadAudio(pendingAudio.blob, fileName);
        setUploading(false);
      }

      const isPublishingNow = form.published && !existingEp?.published;
      const payload = {
        title: form.title.trim(),
        episodeNum: Number(form.episodeNum) || 0,
        season: Number(form.season) || 1,
        guest: form.guest.trim(),
        guestTitle: form.guestTitle.trim(),
        guestBio: form.guestBio.trim(),
        description: form.description.trim(),
        showNotes: form.showNotes.trim(),
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
        published: form.published,
        coverArt: form.coverArt || "",
        audioUrl: audioUrlFinal,
        duration: editingId ? (existingEp?.duration || 0) : (recTime || 0),
        recordedAt: editingId ? (existingEp?.recordedAt || null) : (recordedAt || null),
        recordingLocation: editingId ? (existingEp?.recordingLocation || boothLocation.trim()) : boothLocation.trim(),
        ...(isPublishingNow && { publishedAt: new Date().toISOString() }),
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await setDoc(doc(db, "podcast_episodes", editingId), payload, { merge: true });
        setEpisodes(prev => prev.map(e => e.id === editingId ? { ...e, ...payload } : e));
        if (playerEp?.id === editingId) setPlayerEp(prev => ({ ...prev, ...payload }));
      } else {
        payload.createdAt = serverTimestamp();
        payload.plays = 0;
        const ref2 = await addDoc(collection(db, "podcast_episodes"), payload);
        setEpisodes(prev => [{ id: ref2.id, ...payload }, ...prev]);
        discardRecording();
      }
      setShowEditor(false);
      showToast("Episode saved ✓");
      logAction(
        editingId ? "podcast.update" : "podcast.create",
        form.title.trim(),
        editingId || null
      );
    } catch (err) {
      showToast("Save failed — " + err.message);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const deleteEpisode = async (id) => {
    if (!window.confirm("Delete this episode?")) return;
    const ep = episodes.find(e => e.id === id);
    await deleteDoc(doc(db, "podcast_episodes", id));
    setEpisodes(prev => prev.filter(e => e.id !== id));
    if (playerEp?.id === id) closePlayer();
    logAction("podcast.delete", ep?.title || id, id);
  };

  const downloadEpisode = (ep) => {
    if (!ep.audioUrl) { showToast("No audio file for this episode."); return; }
    /* Firebase Storage ignores the <a download> attribute for cross-origin URLs.
       Instead, append response-content-disposition to make Storage return
       Content-Disposition: attachment — the browser then downloads it directly. */
    const filename = `EP${String(ep.episodeNum).padStart(2, '0')} - ${ep.title}.mp3`;
    const sep = ep.audioUrl.includes('?') ? '&' : '?';
    const url = ep.audioUrl + sep + 'response-content-disposition=' + encodeURIComponent(`attachment; filename="${filename}"`);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const setf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pct = playerDuration > 0 ? (playerTime / playerDuration) * 100 : 0;

  /* ── render ── */
  return (
    <div className="pod-studio">
      {toast && <div className="pod-toast">{toast}</div>}

      {/* HEADER */}
      <div className="pod-header">
        <div className="pod-header-left">
          <div className="pod-brand">
            <span className="pod-brand-icon"><FaPodcast /></span>
            <div>
              <strong>The PP Studio</strong>
              <span>{episodes.length} episode{episodes.length !== 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>
        <div className="pod-header-right">
          <input ref={uploadInputRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={handleFileUpload} />
          <button className="pod-btn pod-btn-ghost" onClick={() => uploadInputRef.current?.click()}>
            <FaUpload /> Upload audio
          </button>
          <button className="pod-btn pod-btn-primary" onClick={openNewEpisode} disabled={!audioBlob && recState !== "done"}>
            <FaPlus /> Save as episode
          </button>
        </div>
      </div>

      <div className={`pod-live-card ${liveSession?.isLive ? "is-live" : liveScheduled ? "is-scheduled" : ""}`}>
        <div className="pod-live-main">
          <span className="pod-live-icon"><FaBroadcastTower /></span>
          <div>
            <strong>{liveSession?.isLive ? "Live podcast is on" : liveScheduled ? "Live podcast scheduled" : "Live podcast room"}</strong>
            <p>{liveSession?.isLive ? (liveSession.title || "Pooja is live now") : liveScheduled ? "Saved and synced to the public podcast page. Edit the fields and press Update schedule." : "Start a real-time audio session for a small live audience."}</p>
            <div className="pod-live-stats">
              <span>{liveSession?.isLive ? fmtTime(liveElapsed) : liveScheduleAt ? new Date(liveScheduleAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "Not scheduled"}</span>
              <span>{liveSession?.status || "idle"}</span>
              <span>{liveListenerCount} listening</span>
              <span>{liveComments.length} comments</span>
            </div>
          </div>
        </div>
        <input
          className="pod-live-title"
          value={liveTitle}
          onChange={e => setLiveTitle(e.target.value)}
          disabled={liveConnected || liveLoading}
          placeholder="Live session title"
        />
        <input
          className="pod-live-title"
          type="datetime-local"
          value={liveScheduleAt}
          onChange={e => setLiveScheduleAt(e.target.value)}
          disabled={liveConnected || liveLoading}
          aria-label="Schedule live podcast"
        />
        <button
          className="pod-btn pod-btn-ghost"
          onClick={saveLiveSchedule}
          disabled={liveLoading || liveConnected}
        >
          <FaCalendarAlt /> {liveScheduled ? "Update schedule" : "Schedule"}
        </button>
        {liveScheduled && (
          <button
            className="pod-btn pod-btn-danger"
            onClick={cancelLiveSchedule}
            disabled={liveLoading || liveConnected}
          >
            <FaTrash /> Cancel
          </button>
        )}
        <button
          className={`pod-btn ${liveConnected || liveSession?.isLive ? "pod-btn-danger" : "pod-btn-live"}`}
          onClick={liveConnected || liveSession?.isLive ? stopLivePodcast : startLivePodcast}
          disabled={liveLoading}
        >
          {liveConnected || liveSession?.isLive ? <><FaStop /> End live</> : <><FaBroadcastTower /> Start live</>}
        </button>
      </div>

      <div className="pod-live-comments-panel">
        <div className="pod-live-comments-head">
          <strong>Live comments</strong>
          <span>{liveComments.length ? "Realtime audience messages" : "Comments will appear here while live"}</span>
        </div>
        <div className="pod-live-comments-list">
          {liveComments.length === 0 ? (
            <p className="pod-live-comments-empty">No live comments yet.</p>
          ) : liveComments.map(c => (
            <div key={c.id} className="pod-live-comment">
              <strong>{c.name}</strong>
              <p>{c.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* RECORDING BOOTH */}
      <div className={`pod-booth ${recState === "recording" ? "active" : ""}`}>
        <div className={`pod-onair ${recState === "recording" ? "live" : ""}`}>
          <span className="pod-onair-dot" />
          {recState === "recording" ? "ON AIR" : recState === "paused" ? "PAUSED" : recState === "done" ? "RECORDED" : "STANDBY"}
        </div>

        <div className="pod-vu-bank pod-vu-left">
          {vuLevels.slice(0, 8).map((lv, i) => (
            <div key={i} className="pod-vu-track"><div className="pod-vu-bar" style={{ "--lv": lv }} /></div>
          ))}
        </div>

        <div className="pod-booth-centre">
          <canvas ref={canvasRef} className="pod-canvas" width={600} height={100} />
          <div className="pod-timer">{fmtTime(recTime)}</div>

          <div className="pod-booth-meta">
            <div className="pod-booth-meta-field">
              <FaMapMarkerAlt className="pod-booth-meta-icon" />
              <input
                className="pod-booth-meta-input"
                value={boothLocation}
                onChange={e => setBoothLocation(e.target.value)}
                placeholder="Studio name / location  (e.g. PP Studio, London)"
              />
            </div>
            {recordedAt && (
              <div className="pod-booth-meta-stamp">
                <FaCalendarAlt className="pod-booth-meta-icon" />
                <span>Recorded {new Date(recordedAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</span>
              </div>
            )}
          </div>

          {micError && <div className="pod-mic-error">{micError}</div>}
          <div className="pod-transport">
            {recState === "idle" && (
              <button className="pod-rec-btn" onClick={startRecording}><FaMicrophone /><span>REC</span></button>
            )}
            {recState === "recording" && (<>
              <button className="pod-ctrl-btn pod-pause" onClick={pauseRecording}><FaPause /></button>
              <button className="pod-ctrl-btn pod-stop" onClick={stopRecording}><FaStop /></button>
            </>)}
            {recState === "paused" && (<>
              <button className="pod-ctrl-btn pod-resume" onClick={resumeRecording}><FaMicrophone /></button>
              <button className="pod-ctrl-btn pod-stop" onClick={stopRecording}><FaStop /></button>
            </>)}
            {recState === "done" && (<>
              <button className="pod-ctrl-btn pod-play" onClick={togglePlay}>{playing ? <FaPause /> : <FaPlay />}</button>
              <button className="pod-ctrl-btn pod-discard" onClick={discardRecording}><FaTrash /></button>
              <button className="pod-ctrl-btn pod-save-ep" onClick={openNewEpisode}><FaCheck /> Save episode</button>
            </>)}
          </div>
        </div>

        <div className="pod-vu-bank pod-vu-right">
          {vuLevels.slice(8, 16).map((lv, i) => (
            <div key={i} className="pod-vu-track"><div className="pod-vu-bar" style={{ "--lv": lv }} /></div>
          ))}
        </div>
      </div>

      {/* EPISODES LIST */}
      <div className="pod-episodes">
        <div className="pod-episodes-head">
          <strong>Episodes</strong>
          <span>{episodes.filter(e => e.published).length} published · {episodes.filter(e => !e.published).length} draft</span>
        </div>
        {loading && <div className="pod-empty"><div className="adm-spinner" style={{ margin: "0 auto" }} /></div>}
        {!loading && episodes.length === 0 && (
          <div className="pod-empty">
            <FaPodcast />
            <p>No episodes yet. Hit REC to record your first one.</p>
          </div>
        )}
        {episodes.map(ep => {
          const isActive = playerEp?.id === ep.id;
          return (
            <div
              key={ep.id}
              className={`pod-ep-row ${ep.published ? "published" : "draft"} ${isActive ? "pod-ep-active" : ""}`}
            >
              {/* Play/pause button */}
              <button
                className={`pod-ep-play-btn ${isActive ? "active" : ""}`}
                onClick={() => loadEpisode(ep)}
                title={isActive && playerPlaying ? "Pause" : "Play"}
                disabled={!ep.audioUrl}
              >
                {isActive && playerPlaying ? <FaPause /> : <FaPlay />}
              </button>

              {ep.coverArt
                ? <img src={ep.coverArt} alt="" className="pod-ep-thumb" />
                : <div className="pod-ep-thumb pod-ep-thumb--placeholder"><FaMicrophone /></div>
              }

              <div className="pod-ep-num">Ep {ep.episodeNum || "—"}</div>

              <div className="pod-ep-body">
                <strong>{ep.title}</strong>
                {ep.guest && <em>with {ep.guest}{ep.guestTitle ? ` · ${ep.guestTitle}` : ""}</em>}
                <div className="pod-ep-rec-meta">
                  {ep.recordedAt && (
                    <span><FaCalendarAlt /> {fmtDate(ep.recordedAt)}</span>
                  )}
                  {ep.recordingLocation && (
                    <span><FaMapMarkerAlt /> {ep.recordingLocation}</span>
                  )}
                </div>
              </div>

              <div className="pod-ep-meta">
                <span className="pod-ep-dur">{fmtDuration(ep.duration)}</span>
                <span className={`pod-ep-status ${ep.published ? "live" : ""}`}>
                  {ep.published ? "Live" : "Draft"}
                </span>
              </div>

              <div className="pod-ep-actions">
                {ep.audioUrl && (
                  <button
                    className="pod-icon-btn"
                    title="Download MP3"
                    onClick={() => downloadEpisode(ep)}
                  >
                    <FaDownload />
                  </button>
                )}
                <button className="pod-icon-btn" title="Edit" onClick={() => openEditEpisode(ep)}>
                  <FaEdit />
                </button>
                <button className="pod-icon-btn pod-icon-danger" title="Delete" onClick={() => deleteEpisode(ep.id)}>
                  <FaTrash />
                </button>
              </div>
            </div>
          );
        })}

        {/* INLINE PLAYER — shows below episodes when one is loaded */}
        {playerEp && (
          <div className="pod-player">
            <div className="pod-player-info">
              <span className="pod-player-ep-num">Ep {playerEp.episodeNum || "—"}</span>
              <div className="pod-player-title-wrap">
                <strong className="pod-player-title">{playerEp.title}</strong>
                {playerEp.guest && <span className="pod-player-guest">with {playerEp.guest}</span>}
              </div>
            </div>

            <div className="pod-player-centre">
              <div className="pod-player-btns">
                <button className="pod-player-skip-btn" onClick={() => skipPlayer(-15)} title="Back 15 seconds">
                  <FaUndo />
                  <span>15</span>
                </button>
                <button className="pod-player-play-btn" onClick={togglePlayerPlay}>
                  {playerPlaying ? <FaPause /> : <FaPlay />}
                </button>
                <button className="pod-player-skip-btn" onClick={() => skipPlayer(15)} title="Forward 15 seconds">
                  <FaRedo />
                  <span>15</span>
                </button>
              </div>

              <div className="pod-player-timeline">
                <span className="pod-player-time">{fmtTime(playerTime)}</span>
                <div
                  ref={progressRef}
                  className="pod-progress-track"
                  onClick={seekPlayer}
                  role="slider"
                  aria-valuemin={0}
                  aria-valuemax={playerDuration}
                  aria-valuenow={playerTime}
                >
                  <div className="pod-progress-fill" style={{ width: `${pct}%` }} />
                  <div className="pod-progress-thumb" style={{ left: `${pct}%` }} />
                </div>
                <span className="pod-player-time">{fmtTime(playerDuration)}</span>
              </div>
            </div>

            <div className="pod-speed-group">
              {SPEEDS.map(s => (
                <button
                  key={s}
                  className={`pod-speed-btn${playerSpeed === s ? " pod-speed-btn--active" : ""}`}
                  onClick={() => changeSpeed(s)}
                >
                  {s === 1 ? "1×" : `${s}×`}
                </button>
              ))}
            </div>

            <button className="pod-player-close" onClick={closePlayer} title="Close player">✕</button>
          </div>
        )}
      </div>

      {/* EPISODE EDITOR DRAWER */}
      {showEditor && (
        <div className="pod-drawer-overlay" onClick={e => e.target === e.currentTarget && setShowEditor(false)}>
          <div className="pod-drawer">
            <div className="pod-drawer-head">
              <strong>{editingId ? "Edit episode" : "New episode"}</strong>
              <button className="pod-drawer-close" onClick={() => setShowEditor(false)}>✕</button>
            </div>
            <div className="pod-drawer-body">
              {pendingAudio && (
                <div className="pod-drawer-audio-badge">
                  <FaMicrophone /> Recorded audio ready — {fmtDuration(recTime)}
                  {recordedAt && (
                    <span className="pod-drawer-rec-stamp">
                      <FaCalendarAlt /> {new Date(recordedAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                      {boothLocation && <> · <FaMapMarkerAlt /> {boothLocation}</>}
                    </span>
                  )}
                </div>
              )}
              <div className="pod-drawer-row">
                <label>Episode title *</label>
                <input className="pod-input" value={form.title} onChange={e => setf("title", e.target.value)} placeholder="e.g. The NDA No One Reads" />
              </div>
              <div className="pod-drawer-grid2">
                <div className="pod-drawer-row">
                  <label>Episode #</label>
                  <input className="pod-input" type="number" value={form.episodeNum} onChange={e => setf("episodeNum", e.target.value)} placeholder="1" />
                </div>
                <div className="pod-drawer-row">
                  <label>Season</label>
                  <input className="pod-input" type="number" value={form.season} onChange={e => setf("season", e.target.value)} placeholder="1" />
                </div>
              </div>
              <div className="pod-drawer-grid2">
                <div className="pod-drawer-row">
                  <label>Guest name</label>
                  <input className="pod-input" value={form.guest} onChange={e => setf("guest", e.target.value)} placeholder="e.g. Sarah Johnson" />
                </div>
                <div className="pod-drawer-row">
                  <label>Guest title</label>
                  <input className="pod-input" value={form.guestTitle} onChange={e => setf("guestTitle", e.target.value)} placeholder="e.g. Startup Founder" />
                </div>
              </div>
              <div className="pod-drawer-row">
                <label>Guest bio <span className="pod-drawer-label-hint">(shown on episode page)</span></label>
                <textarea className="pod-input pod-textarea" value={form.guestBio} onChange={e => setf("guestBio", e.target.value)} placeholder="One or two sentences about the guest…" rows={2} />
              </div>
              <div className="pod-drawer-row">
                <label>Short description <span className="pod-drawer-label-hint">(RSS subtitle, card preview)</span></label>
                <textarea className="pod-input pod-textarea" value={form.description} onChange={e => setf("description", e.target.value)} placeholder="What is this episode about?" rows={2} />
              </div>
              <div className="pod-drawer-row">
                <label>Show notes <span className="pod-drawer-label-hint">(full episode notes, timestamps, links)</span></label>
                <textarea className="pod-input pod-textarea" value={form.showNotes} onChange={e => setf("showNotes", e.target.value)} placeholder={"[00:00] Intro\n[03:45] Main topic\n[12:10] Key takeaway\n\nLinks mentioned: …"} rows={7} />
              </div>
              <div className="pod-drawer-row">
                <label>Tags <span className="pod-drawer-label-hint">(comma separated)</span></label>
                <input className="pod-input" value={form.tags} onChange={e => setf("tags", e.target.value)} placeholder="contracts, freelance, startup" />
              </div>
              <div className="pod-drawer-row">
                <label>Cover art</label>
                <input ref={coverUploadRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCoverUpload} />
                {form.coverArt ? (
                  <div className="pod-cover-preview-wrap">
                    <img src={form.coverArt} alt="Cover" className="pod-cover-preview" />
                    <button className="pod-btn pod-btn-ghost" onClick={() => setf("coverArt", "")}>Remove</button>
                  </div>
                ) : (
                  <button className="pod-btn pod-btn-ghost pod-full" onClick={() => coverUploadRef.current?.click()}>
                    <FaImage /> Upload cover art
                  </button>
                )}
              </div>

              {!pendingAudio && (
                <div className="pod-drawer-row">
                  <label>Upload audio file</label>
                  <input ref={uploadInputRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={handleFileUpload} />
                  <button className="pod-btn pod-btn-ghost pod-full" onClick={() => uploadInputRef.current?.click()}>
                    <FaUpload /> Choose audio file
                  </button>
                </div>
              )}
              <div className="pod-drawer-row">
                <label className="pod-toggle-row">
                  <span>Publish immediately</span>
                  <div className={`pod-toggle ${form.published ? "on" : ""}`} onClick={() => setf("published", !form.published)}>
                    <div className="pod-toggle-knob" />
                  </div>
                </label>
              </div>
              {uploading && (
                <div className="pod-upload-bar">
                  <div className="pod-upload-fill" style={{ width: `${uploadPct}%` }} />
                  <span>Uploading {uploadPct}%</span>
                </div>
              )}
            </div>
            <div className="pod-drawer-foot">
              <button className="pod-btn pod-btn-ghost" onClick={() => setShowEditor(false)}>Cancel</button>
              <button className="pod-btn pod-btn-primary" onClick={saveEpisode} disabled={saving}>
                {saving ? "Saving…" : <><FaCheck /> Save episode</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
